import type {
	Agent,
	AgentTypicalFlow,
	OutboundBusinessHoursWindow,
	OutboundPayload,
	OutboundPlaybook,
	OutboundSchemaFieldDefinition,
	OutboundTaskAttemptOutcome,
} from "@repo/common";
import {
	buildJsonSchemaFromFields,
	DEFAULT_OUTBOUND_INPUT_SCHEMA,
	DEFAULT_OUTBOUND_OUTPUT_SCHEMA,
	fieldsFromJsonSchema,
	OUTBOUND_SCHEMA_FIELD_TYPES,
} from "@repo/common";
import {
	createOutboundPlaybook,
	db,
	deleteOutboundPlaybook,
	outboundPlaybooksCollection,
	updateOutboundPlaybook,
} from "@repo/ui";
import { onSnapshot, query, where } from "firebase/firestore";
import { Edit, Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { useOutboundPlaybookFeatureFlag } from "../hooks/useOutboundPlaybookFeatureFlag";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";

type SchemaMode = "fields" | "json";

type FormState = {
	name: string;
	description: string;
	greeting: string;
	status: "draft" | "enabled" | "disabled";
	conversationInstructions: string;
	typicalFlows: AgentTypicalFlow[];
	inputSchemaMode: SchemaMode;
	inputSchema: string;
	inputSchemaFields: OutboundSchemaFieldDefinition[];
	resultSchemaMode: SchemaMode;
	resultSchema: string;
	resultSchemaFields: OutboundSchemaFieldDefinition[];
	timezone: string;
	businessHours: string;
	initialDelaySeconds: string;
	maxAttempts: string;
	backoffSeconds: string;
	terminalOutcomes: string;
};

const defaultInputSchema = JSON.stringify(
	DEFAULT_OUTBOUND_INPUT_SCHEMA,
	null,
	2,
);
const defaultResultSchema = JSON.stringify(
	DEFAULT_OUTBOUND_OUTPUT_SCHEMA,
	null,
	2,
);
const defaultBusinessHours = JSON.stringify(
	[
		{ dayOfWeek: 1, startTime: "09:00", endTime: "17:00" },
		{ dayOfWeek: 2, startTime: "09:00", endTime: "17:00" },
		{ dayOfWeek: 3, startTime: "09:00", endTime: "17:00" },
		{ dayOfWeek: 4, startTime: "09:00", endTime: "17:00" },
		{ dayOfWeek: 5, startTime: "09:00", endTime: "17:00" },
	],
	null,
	2,
);

function emptyForm(): FormState {
	return {
		name: "",
		description: "",
		greeting: "",
		status: "draft",
		conversationInstructions: "",
		typicalFlows: [],
		inputSchemaMode: "fields",
		inputSchema: defaultInputSchema,
		inputSchemaFields: fieldsFromJsonSchema(DEFAULT_OUTBOUND_INPUT_SCHEMA),
		resultSchemaMode: "fields",
		resultSchema: defaultResultSchema,
		resultSchemaFields: fieldsFromJsonSchema(DEFAULT_OUTBOUND_OUTPUT_SCHEMA),
		timezone: "UTC",
		businessHours: defaultBusinessHours,
		initialDelaySeconds: "0",
		maxAttempts: "3",
		backoffSeconds: "300, 1800, 7200",
		terminalOutcomes: "confirmed, cancelled, wrong_number, failed",
	};
}

function formFromPlaybook(playbook: OutboundPlaybook): FormState {
	return {
		name: playbook.name,
		description: playbook.description || "",
		greeting: playbook.greeting || "",
		status:
			playbook.status === "archived"
				? "disabled"
				: (playbook.status as "draft" | "enabled" | "disabled"),
		conversationInstructions: playbook.conversationInstructions,
		typicalFlows: playbook.typicalFlows || [],
		inputSchemaMode: "fields",
		inputSchema: JSON.stringify(playbook.inputSchema, null, 2),
		inputSchemaFields: fieldsFromJsonSchema(playbook.inputSchema),
		resultSchemaMode: "fields",
		resultSchema: JSON.stringify(playbook.resultSchema, null, 2),
		resultSchemaFields: fieldsFromJsonSchema(playbook.resultSchema),
		timezone: playbook.timezone,
		businessHours: JSON.stringify(playbook.businessHours, null, 2),
		initialDelaySeconds: String(playbook.initialDelaySeconds),
		maxAttempts: String(playbook.retryPolicy.maxAttempts),
		backoffSeconds: playbook.retryPolicy.backoffSeconds.join(", "),
		terminalOutcomes: playbook.terminalOutcomes.join(", "),
	};
}

function parseJsonObject(value: string, label: string): OutboundPayload {
	const parsed = JSON.parse(value);
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
		throw new Error(`${label} must be a JSON object`);
	}
	return parsed as OutboundPayload;
}

function schemaFromForm(
	mode: SchemaMode,
	jsonValue: string,
	fields: OutboundSchemaFieldDefinition[],
	label: string,
): OutboundPayload {
	const schema =
		mode === "json"
			? parseJsonObject(jsonValue, label)
			: buildJsonSchemaFromFields(fields);
	if (
		Object.keys((schema.properties as Record<string, unknown>) || {}).length ===
		0
	) {
		throw new Error(`${label} requires at least one field`);
	}
	return schema;
}

function parseBusinessHours(value: string): OutboundBusinessHoursWindow[] {
	const parsed = JSON.parse(value);
	if (!Array.isArray(parsed))
		throw new Error("Business hours must be an array");
	return parsed as OutboundBusinessHoursWindow[];
}

function parseCsvNumbers(value: string): number[] {
	return value
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => {
			const parsed = Number(part);
			if (!Number.isFinite(parsed) || parsed < 0) {
				throw new Error("Backoff seconds must be comma-separated numbers");
			}
			return parsed;
		});
}

function parseOutcomes(value: string): OutboundTaskAttemptOutcome[] {
	return value
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean) as OutboundTaskAttemptOutcome[];
}

interface OutboundPlaybooksPanelProps {
	agent: Agent;
	className?: string;
	showHeading?: boolean;
}

function inheritedAgentPhoneLabel(
	agent: Agent,
	testingPhoneNumbers: string[],
): string {
	const liveNumbers =
		agent.phoneNumberDetails?.live
			?.filter((assignment) => assignment.status === "active")
			.map((assignment) => assignment.number)
			.filter(Boolean) || [];
	if (liveNumbers.length > 0) return liveNumbers.join(", ");
	if (testingPhoneNumbers.length > 0) return testingPhoneNumbers.join(", ");
	return "—";
}

export function OutboundPlaybooksPanel({
	agent,
	className = "",
	showHeading = true,
}: OutboundPlaybooksPanelProps) {
	const { companies, phoneNumbers = [] } = useData();
	const testingPhoneNumbers = phoneNumbers
		.filter(
			(phoneNumber) =>
				phoneNumber.channel === "test" && phoneNumber.status === "active",
		)
		.map((phoneNumber) => phoneNumber.number)
		.sort((a, b) => a.localeCompare(b));
	const selectedCompanyId = agent.companyId;
	const selectedCompany =
		companies.find((company) => company.id === selectedCompanyId) || null;
	const { enabled, globallyDisabled } =
		useOutboundPlaybookFeatureFlag(selectedCompany);
	const agentId = agent.id;
	const [playbooks, setPlaybooks] = useState<OutboundPlaybook[]>([]);
	const [loading, setLoading] = useState(false);
	const [editorOpen, setEditorOpen] = useState(false);
	const [editing, setEditing] = useState<OutboundPlaybook | null>(null);
	const [form, setForm] = useState<FormState>(emptyForm);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!selectedCompanyId || !enabled) {
			setPlaybooks([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		const q = query(
			outboundPlaybooksCollection(db, selectedCompanyId),
			where("callConfiguration.agentId", "==", agentId),
		);
		return onSnapshot(
			q,
			(snapshot) => {
				setPlaybooks(
					snapshot.docs
						.map((doc) => doc.data())
						.sort(
							(a, b) =>
								((b.updatedAt ? new Date(b.updatedAt).getTime() : 0) || 0) -
								((a.updatedAt ? new Date(a.updatedAt).getTime() : 0) || 0),
						),
				);
				setLoading(false);
			},
			(error) => {
				console.error("Failed to load outbound Playbooks", error);
				toast.error("Failed to load outbound Playbooks");
				setLoading(false);
			},
		);
	}, [agentId, selectedCompanyId, enabled]);

	const enabledCount = useMemo(
		() => playbooks.filter((playbook) => playbook.status === "enabled").length,
		[playbooks],
	);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm());
		setEditorOpen(true);
	};

	const openEdit = (playbook: OutboundPlaybook) => {
		setEditing(playbook);
		setForm(formFromPlaybook(playbook));
		setEditorOpen(true);
	};

	const updateSchemaField = (
		key: "inputSchemaFields" | "resultSchemaFields",
		index: number,
		updates: Partial<OutboundSchemaFieldDefinition>,
	) => {
		setForm((current) => ({
			...current,
			[key]: current[key].map((field, fieldIndex) =>
				fieldIndex === index ? { ...field, ...updates } : field,
			),
		}));
	};

	const addSchemaField = (key: "inputSchemaFields" | "resultSchemaFields") => {
		setForm((current) => ({
			...current,
			[key]: [
				...current[key],
				{
					name: "",
					type: "string",
					required: false,
					description: "",
					enumValues: [],
					showInReport: true,
				},
			],
		}));
	};

	const removeSchemaField = (
		key: "inputSchemaFields" | "resultSchemaFields",
		index: number,
	) => {
		setForm((current) => ({
			...current,
			[key]: current[key].filter((_, fieldIndex) => fieldIndex !== index),
		}));
	};

	const changeSchemaMode = (
		modeKey: "inputSchemaMode" | "resultSchemaMode",
		jsonKey: "inputSchema" | "resultSchema",
		fieldsKey: "inputSchemaFields" | "resultSchemaFields",
		nextMode: SchemaMode,
		label: string,
	) => {
		try {
			setForm((current) => {
				if (nextMode === "json") {
					return {
						...current,
						[modeKey]: nextMode,
						[jsonKey]: JSON.stringify(
							buildJsonSchemaFromFields(current[fieldsKey]),
							null,
							2,
						),
					};
				}

				return {
					...current,
					[modeKey]: nextMode,
					[fieldsKey]: fieldsFromJsonSchema(
						parseJsonObject(current[jsonKey], label),
					),
				};
			});
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			toast.error(message);
		}
	};

	const renderSchemaEditor = (
		label: string,
		modeKey: "inputSchemaMode" | "resultSchemaMode",
		jsonKey: "inputSchema" | "resultSchema",
		fieldsKey: "inputSchemaFields" | "resultSchemaFields",
	) => (
		<div className="space-y-3 md:col-span-2">
			<div className="flex items-center justify-between gap-3">
				<Label>{label}</Label>
				{form[modeKey] === "fields" && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => addSchemaField(fieldsKey)}
					>
						<Plus className="mr-2 h-4 w-4" />
						Add Field
					</Button>
				)}
			</div>
			<Tabs
				value={form[modeKey]}
				onValueChange={(value) =>
					changeSchemaMode(
						modeKey,
						jsonKey,
						fieldsKey,
						value as SchemaMode,
						label,
					)
				}
			>
				<TabsList>
					<TabsTrigger value="fields">Fields</TabsTrigger>
					<TabsTrigger value="json">JSON Schema</TabsTrigger>
				</TabsList>
				<TabsContent value="fields" className="space-y-2">
					<div className="grid gap-2">
						{form[fieldsKey].map((field, index) => (
							<div
								key={`${fieldsKey}-${index}`}
								className="grid gap-2 rounded-md border p-3 md:grid-cols-[1.1fr_1.1fr_150px_120px_150px_1.2fr_1.2fr_40px]"
							>
								<Input
									placeholder="fieldName"
									value={field.name}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											name: event.target.value,
										})
									}
								/>
								<Input
									placeholder="Column Name"
									value={field.columnName || ""}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											columnName: event.target.value,
										})
									}
								/>
								<Select
									value={field.type}
									onValueChange={(
										value: OutboundSchemaFieldDefinition["type"],
									) =>
										updateSchemaField(fieldsKey, index, {
											type: value,
											enumValues: value === "string" ? field.enumValues : [],
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{OUTBOUND_SCHEMA_FIELD_TYPES.map((type) => (
											<SelectItem key={type} value={type}>
												{type === "phone_number" ? "phone number" : type}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Select
									value={field.required ? "required" : "optional"}
									onValueChange={(value) =>
										updateSchemaField(fieldsKey, index, {
											required: value === "required",
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="required">Required</SelectItem>
										<SelectItem value="optional">Optional</SelectItem>
									</SelectContent>
								</Select>
								<Select
									value={field.showInReport !== false ? "show" : "hide"}
									onValueChange={(value) =>
										updateSchemaField(fieldsKey, index, {
											showInReport: value === "show",
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="show">Show in report</SelectItem>
										<SelectItem value="hide">Hide in report</SelectItem>
									</SelectContent>
								</Select>
								<Input
									placeholder="Description"
									value={field.description || ""}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											description: event.target.value,
										})
									}
								/>
								<Input
									placeholder="Enum values"
									value={(field.enumValues || []).join(", ")}
									disabled={field.type !== "string"}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											enumValues: event.target.value
												.split(",")
												.map((value) => value.trim())
												.filter(Boolean),
										})
									}
								/>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									disabled={form[fieldsKey].length <= 1}
									onClick={() => removeSchemaField(fieldsKey, index)}
									aria-label={`Remove ${field.name || "schema field"}`}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						))}
					</div>
				</TabsContent>
				<TabsContent value="json">
					<Textarea
						className="min-h-56 font-mono text-sm"
						value={form[jsonKey]}
						onChange={(event) =>
							setForm({ ...form, [jsonKey]: event.target.value })
						}
					/>
				</TabsContent>
			</Tabs>
		</div>
	);

	const buildPayload = (): Omit<
		OutboundPlaybook,
		"id" | "path" | "createdAt" | "updatedAt"
	> => {
		if (!selectedCompanyId) throw new Error("Company is required");
		if (!agentId) throw new Error("Agent is required");
		if (!form.name.trim()) throw new Error("Name is required");
		if (!form.conversationInstructions.trim()) {
			throw new Error("Conversation instructions are required");
		}

		return {
			companyId: selectedCompanyId,
			name: form.name.trim(),
			description: form.description.trim() || null,
			greeting: form.greeting.trim() || null,
			status: form.status,
			version: editing?.version || 1,
			conversationInstructions: form.conversationInstructions.trim(),
			typicalFlows: (form.typicalFlows || []).filter(
				(flow) =>
					flow.name.trim() || flow.description.trim() || flow.example.trim(),
			),
			inputSchema: schemaFromForm(
				form.inputSchemaMode,
				form.inputSchema,
				form.inputSchemaFields,
				"Input schema",
			),
			resultSchema: schemaFromForm(
				form.resultSchemaMode,
				form.resultSchema,
				form.resultSchemaFields,
				"Output schema",
			),
			timezone: form.timezone.trim() || "UTC",
			businessHours: parseBusinessHours(form.businessHours),
			initialDelaySeconds: Number(form.initialDelaySeconds || 0),
			retryPolicy: {
				maxAttempts: Number(form.maxAttempts || 1),
				backoffSeconds: parseCsvNumbers(form.backoffSeconds),
			},
			terminalOutcomes: parseOutcomes(form.terminalOutcomes),
			callConfiguration: {
				agentId,
				serviceId: agent.serviceId || null,
				fromPhoneNumber: null,
				workerType: "outbound_call",
			},
			enabledAt:
				form.status === "enabled" ? (editing?.enabledAt ?? new Date()) : null,
			disabledAt:
				form.status === "disabled" ? (editing?.disabledAt ?? new Date()) : null,
			archivedAt: null,
			createdBy: editing?.createdBy || null,
			updatedBy: null,
		};
	};

	const save = async () => {
		setSaving(true);
		try {
			const payload = buildPayload();
			if (editing?.id) {
				await updateOutboundPlaybook(
					db,
					selectedCompanyId,
					editing.id,
					payload,
				);
				toast.success("Outbound Playbook updated");
			} else {
				await createOutboundPlaybook(db, payload);
				toast.success("Outbound Playbook created");
			}
			setEditorOpen(false);
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			toast.error(message);
		} finally {
			setSaving(false);
		}
	};

	const remove = async (playbook: OutboundPlaybook) => {
		if (!selectedCompanyId || !playbook.id) return;
		if (!window.confirm(`Delete "${playbook.name}"?`)) return;
		await deleteOutboundPlaybook(db, selectedCompanyId, playbook.id);
		toast.success("Outbound Playbook deleted");
	};

	if (globallyDisabled) return null;

	return (
		<div className={`space-y-6 ${className}`.trim()}>
			<div className="flex flex-wrap items-start justify-between gap-4">
				{showHeading && (
					<div>
						<h3 className="text-lg font-medium">Outbound Playbooks</h3>
						<p className="text-sm text-slate-600">
							Manage outbound call flows for {agent.name || "this agent"}.
						</p>
					</div>
				)}
				<Button onClick={openCreate} disabled={!enabled}>
					<Plus className="mr-2 h-4 w-4" />
					New Playbook
				</Button>
			</div>

			<div className="grid gap-3 rounded-md border bg-slate-50 p-3 text-sm text-slate-700 md:grid-cols-4">
				<div>
					<div className="font-medium text-slate-900">Company</div>
					<div>{selectedCompany?.name || agent.companyId}</div>
				</div>
				<div>
					<div className="font-medium text-slate-900">Agent</div>
					<div>{agent.name || "Unnamed agent"}</div>
				</div>
				<div>
					<div className="font-medium text-slate-900">Service</div>
					<div>{agent.service?.name || agent.serviceId || "No service"}</div>
				</div>
				<div>
					<div className="font-medium text-slate-900">Outbound number</div>
					<div dir="ltr">
						{inheritedAgentPhoneLabel(agent, testingPhoneNumbers)}
					</div>
				</div>
				<div>
					<div className="text-2xl font-semibold text-slate-900">
						{playbooks.length}
					</div>
					<div>Total Playbooks</div>
				</div>
				<div>
					<div className="text-2xl font-semibold text-slate-900">
						{enabledCount}
					</div>
					<div>Enabled</div>
				</div>
				{!enabled && (
					<div className="flex items-end">
						<Badge variant="outline">Feature disabled for company</Badge>
					</div>
				)}
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Playbooks</CardTitle>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Timezone</TableHead>
								<TableHead>Attempts</TableHead>
								<TableHead className="w-32">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={5}>Loading...</TableCell>
								</TableRow>
							) : playbooks.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5}>
										No outbound Playbooks found.
									</TableCell>
								</TableRow>
							) : (
								playbooks.map((playbook) => (
									<TableRow key={playbook.id}>
										<TableCell>
											<div className="font-medium">{playbook.name}</div>
											<div className="line-clamp-2 text-sm text-slate-500">
												{playbook.description}
											</div>
										</TableCell>
										<TableCell>
											<Badge variant="outline">{playbook.status}</Badge>
										</TableCell>
										<TableCell>{playbook.timezone}</TableCell>
										<TableCell>{playbook.retryPolicy.maxAttempts}</TableCell>
										<TableCell>
											<div className="flex gap-1">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => openEdit(playbook)}
													aria-label="Edit Playbook"
												>
													<Edit className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => remove(playbook)}
													aria-label="Delete Playbook"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Dialog open={editorOpen} onOpenChange={setEditorOpen}>
				<DialogContent className="admin-editor-dialog admin-editor-dialog--workspace">
					<DialogHeader>
						<DialogTitle>
							{editing ? "Edit Outbound Playbook" : "New Outbound Playbook"}
						</DialogTitle>
					</DialogHeader>
					<div className="admin-editor-body grid gap-4 md:grid-cols-2">
						<div className="space-y-2">
							<Label>Name</Label>
							<Input
								value={form.name}
								onChange={(event) =>
									setForm({ ...form, name: event.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label>Status</Label>
							<Select
								value={form.status}
								onValueChange={(value: FormState["status"]) =>
									setForm({ ...form, status: value })
								}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="draft">Draft</SelectItem>
									<SelectItem value="enabled">Enabled</SelectItem>
									<SelectItem value="disabled">Disabled</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label>Description</Label>
							<Input
								value={form.description}
								onChange={(event) =>
									setForm({ ...form, description: event.target.value })
								}
							/>
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label>Greeting</Label>
							<Textarea
								className="min-h-20"
								value={form.greeting}
								placeholder="Optional playbook-specific opening. Leave empty for the realtime model to decide."
								onChange={(event) =>
									setForm({ ...form, greeting: event.target.value })
								}
							/>
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label>Agent Conversation Instructions</Label>
							<Textarea
								className="min-h-32"
								value={form.conversationInstructions}
								onChange={(event) =>
									setForm({
										...form,
										conversationInstructions: event.target.value,
									})
								}
							/>
						</div>
						{renderSchemaEditor(
							"Input Schema",
							"inputSchemaMode",
							"inputSchema",
							"inputSchemaFields",
						)}
						{renderSchemaEditor(
							"Structured Result Schema",
							"resultSchemaMode",
							"resultSchema",
							"resultSchemaFields",
						)}
						<div className="space-y-2">
							<Label>Timezone</Label>
							<Input
								value={form.timezone}
								onChange={(event) =>
									setForm({ ...form, timezone: event.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label>Initial Delay Seconds</Label>
							<Input
								type="number"
								min="0"
								value={form.initialDelaySeconds}
								onChange={(event) =>
									setForm({
										...form,
										initialDelaySeconds: event.target.value,
									})
								}
							/>
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label>Business Hours</Label>
							<Textarea
								className="min-h-28 font-mono text-sm"
								value={form.businessHours}
								onChange={(event) =>
									setForm({ ...form, businessHours: event.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label>Max Attempts</Label>
							<Input
								type="number"
								min="1"
								value={form.maxAttempts}
								onChange={(event) =>
									setForm({ ...form, maxAttempts: event.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label>Backoff Seconds</Label>
							<Input
								value={form.backoffSeconds}
								onChange={(event) =>
									setForm({ ...form, backoffSeconds: event.target.value })
								}
							/>
						</div>
						<div className="space-y-2 md:col-span-2">
							<Label>Terminal Outcomes</Label>
							<Input
								value={form.terminalOutcomes}
								onChange={(event) =>
									setForm({ ...form, terminalOutcomes: event.target.value })
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setEditorOpen(false)}>
							Cancel
						</Button>
						<Button onClick={save} disabled={saving || !enabled}>
							{saving ? "Saving..." : "Save Playbook"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
